import React from 'react';
import { Box } from 'edenred-ui';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { FormStepMode } from '@epi-models/pages/Transfer';
import { FormStepStyles } from '@epi-pages/LoadBenefit/components/shared/FormStep/FormStep.styles';
import { FormStepLabel } from '@epi-pages/LoadBenefit/components/shared/FormStepLabel';
import { TopupShowTable } from '@epi-pages/LoadBenefit/components/Topup/TopupForm/TopupShowTable';
import { TopupSummary } from '@epi-pages/LoadBenefit/components/Topup/TopupSummary';
import { Payment } from '@epi-constants/benefitTypes';
import { TopupSwitch } from '@epi-pages/LoadBenefit/components/Topup/TopupForm/TopupSwitch';
import { EmployeesTable } from '@epi-pages/LoadBenefit/components/Topup/TopupForm/EmployeesTable';
import { FormStep } from '@epi-pages/LoadBenefit/components/shared/FormStep';
import { useTopupEmployees } from '@epi-pages/LoadBenefit/hooks';
import { FormFields } from '@epi-forms/helpers';
import { useFormContext } from 'react-hook-form';
import { getTopupTable } from '@epi-store/topup/topup.selectors';

type LoadEmployeesFormProps = Pick<
  ReturnType<typeof useTopupEmployees>,
  | 'paginationCount'
  | 'toggleEmployee'
  | 'checkedCount'
  | 'toggleAllEmployee'
  | 'isAllEmployeeChecked'
  | 'formattedTopup'
  | 'formattedFee'
  | 'formattedVat'
  | 'formattedTopupWithFees'
  | 'formattedRemainingBalance'
  | 'remainingBalance'
  | 'fee'
  | 'vat'
> & {
  isLunchTopupRequest: boolean;
  isToggle: boolean;
  showOnHoldBtn: boolean;
  handleToggler: () => void;
  stepNumber?: number;
  showRemainingBalance?: boolean;
};

export const LoadEmployeesForm = ({
  formattedTopup,
  formattedFee,
  formattedVat,
  formattedTopupWithFees,
  formattedRemainingBalance,
  remainingBalance,
  fee,
  vat,
  toggleAllEmployee,
  isAllEmployeeChecked,
  paginationCount,
  toggleEmployee,
  checkedCount,
  isLunchTopupRequest,
  handleToggler,
  isToggle,
  showOnHoldBtn,
  stepNumber = 4,
  showRemainingBalance
}: LoadEmployeesFormProps) => {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const paymentMethod = watch(FormFields.PaymentMethod);
  const showTable = useSelector(getTopupTable);
  const shouldShowRemainingBalance =
    showRemainingBalance ?? paymentMethod === Payment.Deduction;

  return (
    <FormStep mode={FormStepMode.Outlined} stepNumber={stepNumber}>
      <Box flex="1" width="100%" overflow="auto">
        <FormStepStyles.Content pr="310px">
          <Box
            display="flex"
            flexDirection="column"
            alignSelf="stretch"
            justifyContent="space-between"
          >
            <FormStepLabel
              mode={FormStepMode.Outlined}
              stepNumber={stepNumber}
              stepTitle={
                isLunchTopupRequest
                  ? t(
                      'containers.deposit_money.topup_tab.form.employees.title_lounari',
                      {
                        employeesNumber: checkedCount
                      }
                    )
                  : t(
                      'containers.deposit_money.topup_tab.form.employees.title',
                      {
                        employeesNumber: checkedCount
                      }
                    )
              }
            >
              {!!paginationCount && <TopupShowTable />}
            </FormStepLabel>
          </Box>
          <TopupSummary
            formattedTopup={formattedTopup}
            formattedFee={formattedFee}
            formattedVat={formattedVat}
            formattedTopupWithFees={formattedTopupWithFees}
            formattedRemainingBalance={formattedRemainingBalance}
            remainingBalance={remainingBalance}
            fee={fee}
            vat={vat}
            showRemainingBalance={shouldShowRemainingBalance}
          />
        </FormStepStyles.Content>
        {showOnHoldBtn && (
          <TopupSwitch checked={isToggle} onChange={handleToggler} />
        )}
        {showTable && (
          <EmployeesTable
            toggleAllEmployee={toggleAllEmployee}
            paginationCount={paginationCount}
            toggleEmployee={toggleEmployee}
            checkedCount={checkedCount}
            isAllEmployeeChecked={isAllEmployeeChecked}
            isLunchTopupRequest={isLunchTopupRequest}
          />
        )}
      </Box>
    </FormStep>
  );
};
